import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type {
  InternationalTournament,
  TournamentGroup,
  GroupMatch,
  GroupStanding,
} from "@/data/internationalTournaments";

export interface ScrapedWorldsGame {
  date: string;
  team1: string;
  team2: string;
  score1: number | null;
  score2: number | null;
  status: "upcoming" | "live" | "finished";
  isOvertime: boolean;
  isShootout: boolean;
  venue: string;
  group: string;
  timeLocal: string;
  gameCenter?: string;
}

interface ScrapeResult {
  games: ScrapedWorldsGame[];
  scrapedAt: string | null;
  stale?: boolean;
}

const fetchScraped = async (): Promise<ScrapeResult> => {
  try {
    const { data, error } = await supabase.functions.invoke("worlds-scrape");
    if (error) {
      console.warn("worlds-scrape error:", error);
      return { games: [], scrapedAt: null };
    }
    return data ?? { games: [], scrapedAt: null };
  } catch (err) {
    console.warn("worlds-scrape fetch failed:", err);
    return { games: [], scrapedAt: null };
  }
};

/** Standalone hook exposing only the scraped IIHF Worlds games (no tournament merge). */
export const useWorldsScrape = (enabled = true) => {
  return useQuery({
    queryKey: ["worlds-scrape-2026"],
    queryFn: fetchScraped,
    enabled,
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
};

/** Normalize "May 15 CET" / "May 15" → "May 15" */
const normDate = (d?: string) => (d ?? "").replace(/\s*CET.*/, "").trim();

function recomputeStandings(matches: GroupMatch[], teams: string[]): GroupStanding[] {
  const map = new Map<string, GroupStanding>(
    teams.map(t => [t, { team: t, gp: 0, w: 0, otw: 0, otl: 0, l: 0, gf: 0, ga: 0, pts: 0 }])
  );
  for (const m of matches) {
    if (m.score1 == null || m.score2 == null) continue;
    const a = map.get(m.team1);
    const b = map.get(m.team2);
    if (!a || !b) continue;
    a.gp++; b.gp++;
    a.gf += m.score1; a.ga += m.score2;
    b.gf += m.score2; b.ga += m.score1;
    const ot = m.isOvertime || m.isShootout;
    if (m.score1 > m.score2) {
      if (ot) { a.otw++; a.pts += 2; b.otl++; b.pts += 1; }
      else    { a.w++;   a.pts += 3; b.l++; }
    } else {
      if (ot) { b.otw++; b.pts += 2; a.otl++; a.pts += 1; }
      else    { b.w++;   b.pts += 3; a.l++; }
    }
  }
  return [...map.values()].sort((x, y) =>
    y.pts - x.pts || (y.gf - y.ga) - (x.gf - x.ga) || y.gf - x.gf
  );
}

function mergeGroup(group: TournamentGroup, scraped: ScrapedWorldsGame[]): TournamentGroup {
  const groupLetter = group.name.match(/Group\s+([A-Z])/i)?.[1]?.toUpperCase();
  const candidates = scraped.filter(g => !groupLetter || g.group === groupLetter);
  if (!candidates.length) return group;

  const used = new Set<number>();
  const matches = group.matches.map(m => {
    const idx = candidates.findIndex((s, i) =>
      !used.has(i) &&
      normDate(s.date) === normDate(m.date) &&
      ((s.team1 === m.team1 && s.team2 === m.team2) ||
       (s.team1 === m.team2 && s.team2 === m.team1))
    );
    if (idx === -1) return m;
    used.add(idx);
    const s = candidates[idx];
    if (s.score1 == null || s.score2 == null) return m;
    const swapped = s.team1 === m.team2;
    return {
      ...m,
      score1: swapped ? s.score2 : s.score1,
      score2: swapped ? s.score1 : s.score2,
      isOvertime: s.isOvertime || m.isOvertime,
      isShootout: s.isShootout || m.isShootout,
    };
  });

  const teams = group.standings.map(s => s.team);
  const standings = recomputeStandings(matches, teams);

  return { ...group, matches, standings };
}

/** Merge live scraped scores into knockout rounds (QF/SF/Medal games). */
function mergeKnockout(
  rounds: InternationalTournament["knockoutRounds"],
  scraped: ScrapedWorldsGame[],
): InternationalTournament["knockoutRounds"] {
  return rounds.map(round => ({
    ...round,
    matchups: round.matchups.map(m => {
      if (m.team1 === "TBD" || m.team2 === "TBD") return m;
      const s = scraped.find(g =>
        normDate(g.date) === normDate(m.date) &&
        ((g.team1 === m.team1 && g.team2 === m.team2) ||
         (g.team1 === m.team2 && g.team2 === m.team1))
      );
      if (!s || s.score1 == null || s.score2 == null) return m;
      const swapped = s.team1 === m.team2;
      return {
        ...m,
        score1: swapped ? s.score2 : s.score1,
        score2: swapped ? s.score1 : s.score2,
        isOvertime: s.isOvertime || m.isOvertime,
        isShootout: s.isShootout || m.isShootout,
        status: s.status === "finished" ? "completed" as const : m.status,
      };
    }),
  }));
}

/**
 * Wrap a tournament with live scraped IIHF data (currently 2026 Men's Worlds only).
 */
export const useWorldsLive = (
  tournament: InternationalTournament | undefined,
  enabled: boolean,
) => {
  const query = useQuery({
    queryKey: ["worlds-scrape-2026"],
    queryFn: fetchScraped,
    enabled,
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  if (!tournament || !enabled || !query.data?.games?.length) {
    return { tournament, scrapedAt: query.data?.scrapedAt ?? null, isStale: query.data?.stale ?? false, isLoading: query.isLoading };
  }

  const games = query.data.games;
  const merged: InternationalTournament = {
    ...tournament,
    groupStage: tournament.groupStage
      ? tournament.groupStage.map(g => mergeGroup(g, games))
      : tournament.groupStage,
    knockoutRounds: mergeKnockout(tournament.knockoutRounds, games),
  };

  return {
    tournament: merged,
    scrapedAt: query.data.scrapedAt,
    isStale: query.data.stale ?? false,
    isLoading: query.isLoading,
  };
};
